import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { z } from "zod";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import FormTemplate from "../../templates/FormTemplate";


export type PopularPlaceFormData = {
  name: string;
  name_kh?: string;
  name_zh?: string;
};

type Props = {
  handleSubmit: (
    data: PopularPlaceFormData,
    onError: (message: string) => void
  ) => void;
  defaultValues?: Partial<PopularPlaceFormData>;
  mode?: "create" | "edit";
};

export const PopularPlaceForm: React.FC<Props> = ({
  handleSubmit,
  defaultValues = {},
  mode = "create",
}) => {
  const [hasNameError, setHasNameError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  // Local submission state to prevent multiple clicks
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(() => {
    return z.object({
      name: z.string().min(1, "Name is required"),
      name_kh: z.string().min(1, "Name (Khmer) is required"),
      name_zh: z.string().min(1, "Name (Chinese) is required")
    });
  }, []);

  return (
    <FormTemplate
      schema={schema}
      setMethods={() => { }}
      isSubmitting={isSubmitting}
      defaultValues={{
        name: defaultValues.name || "",
        name_kh: defaultValues.name_kh || "",
        name_zh: defaultValues.name_zh || "",
      }}
      onSubmit={async (data) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);

        try {
          // Reset errors
          setHasNameError(false);
          setErrorMessage(undefined);

          // Wrap handleSubmit in a Promise
          await new Promise<void>((resolve, reject) => {
            handleSubmit(data as PopularPlaceFormData, (message) => {
              setErrorMessage(message);
              reject(new Error(message));
            });

            // For successful submissions, resolve the promise
            setTimeout(resolve, 100);
          });
        } catch (error: any) {
          // Error is already handled by setting errorMessage above
          console.log('PopularPlace form submission error handled:', error.message);
          setIsSubmitting(false);
        }
        // Don't reset isSubmitting on success - let the modal close handle it
      }}
    >
      <StyledFormGrid>
        <StyledInputContainer>
          <Input
            label="Popular place name"
            name="name"
            type="text"
            placeholder="Enter name"
            autoFocus
            required
            hasError={hasNameError}
            errorMessage={hasNameError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <Input
            label="Name (Khmer)"
            name="name_kh"
            type="text"
            placeholder="Enter Name (Khmer)"
            hasError={false}
            required
            fullWidth
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <Input
            label="Name (Chinese)"
            name="name_zh"
            type="text"
            placeholder="Enter Name (Chinese)"
            hasError={false}
            required
            fullWidth
          />
        </StyledInputContainer>
      </StyledFormGrid>

      <StyledFooter>
        <Button
          variant="filled"
          text={isSubmitting ? (mode === "create" ? "Creating..." : "Updating...") : (mode === "create" ? "Create" : "Update")}
          type="submit"
          isSubmitting={isSubmitting}
        />
      </StyledFooter>
    </FormTemplate>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;
